/**
 * useLocationActivity - React Hook for SignalR Location Activity
 * 
 * Provides real-time location activity events via SignalR WebSocket connection.
 * Includes automatic reconnection with exponential backoff and connection quality monitoring.
 * 
 * @author BizClick WMS
 * @version 1.0 - Fase 2 Implementation
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import {
  LocationActivityEvent,
  ConnectionStatus,
  ConnectionQuality,
  UseLocationActivityReturn
} from '../types/locationActivity';
import { AuthService } from '../services/authService';

/**
 * Get JWT token from localStorage
 */
const getAuthToken = (): string | null => {
  return localStorage.getItem('bizclick_token');
};

/**
 * Get SignalR hub URL from environment
 */
const getHubUrl = (): string => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:7141/api/v1';
  // Remove /api/v1 suffix and append hub path
  const baseUrl = apiUrl.replace('/api/v1', '');
  return `${baseUrl}/hubs/locationactivity`;
};

/**
 * Custom hook for Location Activity SignalR connection
 * 
 * @param onActivityReceived - Callback when new activity event is received
 * @param onConnectionStateChanged - Callback when connection state changes
 * @returns Hook API for managing SignalR connection
 */
export const useLocationActivity = (
  onActivityReceived?: (event: LocationActivityEvent) => void,
  onConnectionStateChanged?: (status: ConnectionStatus) => void
): UseLocationActivityReturn => {
  // Connection state
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(ConnectionStatus.Disconnected);
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>({
    latency: 0,
    packetLoss: 0
  });
  const [lastEvent, setLastEvent] = useState<LocationActivityEvent | null>(null);
  
  // Refs
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000; // 1 second
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastEventIdRef = useRef<string>(''); // Track last event to prevent duplicates
  
  /**
   * Update connection status and notify listeners
   */
  const updateConnectionStatus = useCallback((status: ConnectionStatus) => {
    setConnectionStatus(status);
    if (onConnectionStateChanged) {
      onConnectionStateChanged(status);
    }
  }, [onConnectionStateChanged]);
  
  /**
   * Calculate exponential backoff delay for reconnection
   */
  const getReconnectDelay = useCallback((): number => {
    const delay = baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
    return Math.min(delay, 30000); // Max 30 seconds
  }, []);
  
  /**
   * Ping server to measure latency
   */
  const pingServer = useCallback(async () => {
    if (!connectionRef.current || connectionStatus !== ConnectionStatus.Connected) {
      return;
    }
    
    try {
      const startTime = Date.now();
      // Simple echo method to measure round-trip time
      await connectionRef.current.invoke('Ping');
      const latency = Date.now() - startTime;
      
      setConnectionQuality(prev => ({
        ...prev,
        latency,
        lastPingTimestamp: new Date().toISOString()
      }));
    } catch (error) {
      console.warn('[SignalR] Ping failed:', error);
      setConnectionQuality(prev => ({
        ...prev,
        packetLoss: Math.min(prev.packetLoss + 10, 100)
      }));
    }
  }, [connectionStatus]);
  
  /**
   * Start periodic ping to monitor connection quality
   */
  const startPingMonitoring = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    
    pingIntervalRef.current = setInterval(() => {
      pingServer();
    }, 5000); // Ping every 5 seconds
  }, [pingServer]);
  
  /**
   * Stop ping monitoring
   */
  const stopPingMonitoring = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);
  
  /**
  * Join a warehouse group to receive warehouse-specific events
  */
const joinWarehouseGroup = useCallback(async (warehouseId: string) => {
  if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
  throw new Error('SignalR connection not established');
  }
  
  try {
  // Get companyNo from JWT token
  const userData = AuthService.getUserDataFromToken();
    if (!userData) {
    throw new Error('Unable to get user data from token');
  }
    
    await connectionRef.current.invoke('JoinWarehouseGroup', userData.companyNo, warehouseId);
    console.log(`[SignalR] Joined warehouse group: ${userData.companyNo}/${warehouseId}`);
  } catch (error) {
    console.error('[SignalR] Failed to join warehouse group:', error);
    throw error;
  }
}, [connectionStatus]);
  
  /**
  * Join a rack group to receive rack-specific events
  */
const joinRackGroup = useCallback(async (warehouseId: string, rackNumber: number) => {
  if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
  throw new Error('SignalR connection not established');
  }
  
  try {
  // Get companyNo from JWT token
  const userData = AuthService.getUserDataFromToken();
    if (!userData) {
    throw new Error('Unable to get user data from token');
  }
    
    await connectionRef.current.invoke('JoinRackGroup', userData.companyNo, warehouseId, rackNumber);
    console.log(`[SignalR] Joined rack group: ${userData.companyNo}/${warehouseId}/Rack-${rackNumber}`);
  } catch (error) {
    console.error('[SignalR] Failed to join rack group:', error);
    throw error;
  }
}, [connectionStatus]);
  
  /**
   * Leave a specific group
   */
  const leaveGroup = useCallback(async (groupName: string) => {
    if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR connection not established');
    }
    
    try {
      await connectionRef.current.invoke('LeaveGroup', groupName);
      console.log(`[SignalR] Left group: ${groupName}`);
    } catch (error) {
      console.error('[SignalR] Failed to leave group:', error);
      throw error;
    }
  }, [connectionStatus]);
  
  /**
   * Manually disconnect from SignalR
   */
  const disconnect = useCallback(() => {
    if (connectionRef.current) {
      stopPingMonitoring();
      connectionRef.current.stop();
      console.log('[SignalR] Manual disconnect');
    }
  }, [stopPingMonitoring]);
  
  /**
   * Initialize SignalR connection
   */
  useEffect(() => {
    const token = getAuthToken();
    
    if (!token) {
      console.warn('[SignalR] No authentication token found, cannot connect');
      return;
    }
    
    // Prevent multiple connections in React StrictMode
    if (connectionRef.current) {
      console.log('[SignalR] Connection already exists, skipping initialization');
      return;
    }
    
    const hubUrl = getHubUrl();
    console.log(`[SignalR] Initializing connection to: ${hubUrl}`);
    
    // Build SignalR connection
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => token,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents,
        skipNegotiation: false
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext: signalR.RetryContext) => {
          reconnectAttemptsRef.current = retryContext.previousRetryCount;
          
          if (retryContext.previousRetryCount >= maxReconnectAttempts) {
            console.error('[SignalR] Max reconnection attempts reached');
            return null; // Stop reconnecting
          }
          
          const delay = getReconnectDelay();
          console.log(`[SignalR] Reconnecting in ${delay}ms (attempt ${retryContext.previousRetryCount + 1}/${maxReconnectAttempts})`);
          return delay;
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();
    
    connectionRef.current = connection;
    
    // Register event handlers
    // Backend broadcasts with 'ActivityReceived' method name
    connection.on('ActivityReceived', (event: LocationActivityEvent) => {
      console.log('[SignalR] Activity received:', event);
      
      // Duplicate detection: Create unique ID from event properties
      const eventId = `${event.timestamp}_${event.binCode}_${event.itemNo}_${event.quantity}`;
      
      if (eventId === lastEventIdRef.current) {
        console.warn('[SignalR] Duplicate event detected, ignoring:', eventId);
        return; // Skip duplicate
      }
      
      lastEventIdRef.current = eventId;
      setLastEvent(event);
      
      if (onActivityReceived) {
        onActivityReceived(event);
      }
      
      // Reset packet loss on successful message
      setConnectionQuality(prev => ({
        ...prev,
        packetLoss: Math.max(prev.packetLoss - 5, 0)
      }));
    });
    
    // Connection lifecycle events
    connection.onclose((error?: Error) => {
      console.warn('[SignalR] Connection closed', error);
      updateConnectionStatus(ConnectionStatus.Disconnected);
      stopPingMonitoring();
      reconnectAttemptsRef.current = 0;
    });
    
    connection.onreconnecting((error?: Error) => {
      console.warn('[SignalR] Connection lost, reconnecting...', error);
      updateConnectionStatus(ConnectionStatus.Reconnecting);
      stopPingMonitoring();
    });
    
    connection.onreconnected((connectionId?: string) => {
      console.log('[SignalR] Reconnected successfully', connectionId);
      updateConnectionStatus(ConnectionStatus.Connected);
      reconnectAttemptsRef.current = 0;
      
      // Reset connection quality
      setConnectionQuality({
        latency: 0,
        packetLoss: 0,
        lastPingTimestamp: new Date().toISOString()
      });
      
      startPingMonitoring();
    });
    
    // Start connection
    connection.start()
      .then(() => {
        console.log('[SignalR] Connected successfully');
        updateConnectionStatus(ConnectionStatus.Connected);
        reconnectAttemptsRef.current = 0;
        startPingMonitoring();
      })
      .catch((error: Error) => {
        console.error('[SignalR] Connection failed:', error);
        updateConnectionStatus(ConnectionStatus.Disconnected);
        
        // Attempt manual reconnection after delay
        setTimeout(() => {
          // Skip if cleanup has nulled the ref (component unmounted) or if a new connection
          // has taken over. Without this guard we'd resurrect a dead connection and the
          // ref would be stale, causing joinWarehouseGroup to fail with "not established".
          if (connectionRef.current !== connection) {
            console.log('[SignalR] Skipping manual reconnect - connection no longer current');
            return;
          }
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectAttemptsRef.current++;
            console.log(`[SignalR] Manual reconnection attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts}`);
            connection.start()
              .then(() => {
                console.log('[SignalR] Manual reconnection successful');
                connectionRef.current = connection; // Restore ref in case cleanup nulled it
                updateConnectionStatus(ConnectionStatus.Connected);
                reconnectAttemptsRef.current = 0;
                startPingMonitoring();
              })
              .catch((err: Error) => {
                console.error('[SignalR] Manual reconnection failed:', err);
              });
          }
        }, getReconnectDelay());
      });
    
    // Cleanup on unmount
    return () => {
      console.log('[SignalR] Cleaning up connection');
      stopPingMonitoring();

      // Null the ref synchronously so a re-mount (e.g. React StrictMode) doesn't
      // see a stale "already exists" reference and skip initialization.
      const conn = connectionRef.current;
      connectionRef.current = null;
      if (conn) {
        conn.stop().then(() => {
          console.log('[SignalR] Connection stopped and cleaned up');
        });
      }
    };
  }, []); // Empty deps - run once on mount
  
  return {
    connectionStatus,
    connectionQuality,
    lastEvent,
    joinWarehouseGroup,
    joinRackGroup,
    leaveGroup,
    disconnect
  };
};
